'use client';
import React, { useEffect, useRef, useState } from 'react';
import { fetchPunkImages, getPunkImageUrl, LOCAL_FALLBACK, DEFAULT_PUNK_IMAGES } from '@/lib/punkImages';

function PunkAvatar({ index, punkUrls, size = 28 }: { index: number; punkUrls: string[]; size?: number }) {
  const urls = punkUrls.length > 0 ? punkUrls : DEFAULT_PUNK_IMAGES;
  const primarySrc = getPunkImageUrl(urls, index);
  const [src, setSrc] = useState(primarySrc);
  const [triedFallback, setTriedFallback] = useState(false);

  useEffect(() => {
    const newSrc = getPunkImageUrl(punkUrls.length > 0 ? punkUrls : DEFAULT_PUNK_IMAGES, index);
    setSrc(newSrc);
    setTriedFallback(false);
  }, [index, punkUrls]);

  const handleError = () => {
    if (!triedFallback) {
      setTriedFallback(true);
      setSrc(LOCAL_FALLBACK[index % LOCAL_FALLBACK.length]);
    }
  };

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={src}
      alt={`Zec Punk #${(index % 50) + 1}`}
      width={size}
      height={size}
      onError={handleError}
      style={{
        imageRendering: 'pixelated',
        borderRadius: 4,
        display: 'block',
        width: size,
        height: size,
        objectFit: 'cover',
        background: '#1b1b1d',
      }}
    />
  );
}

function genUniqueAddr(seed: number): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let hash = seed * 2654435761;
  let out = 'u1';
  for (let i = 0; i < 93; i++) {
    hash = ((hash ^ (hash >>> 16)) * 2246822519) >>> 0;
    hash = ((hash ^ (hash >>> 13)) * 3266489917) >>> 0;
    hash = (hash ^ (hash >>> 16)) >>> 0;
    out += chars[(hash + i * 37 + seed) % chars.length];
  }
  return out;
}

function truncateAddr(addr: string) {
  return addr.slice(0, 10) + '...' + addr.slice(-8);
}

interface MintEntry {
  id: number;
  tokenId: number;
  addr: string;
  ts: number;
  isNew?: boolean;
  punkIndex: number;
}

const INITIAL_ENTRIES: MintEntry[] = Array.from({ length: 14 }, (_, i) => ({
  id: i,
  tokenId: 1000 + i * 97 + i * 13,
  addr: genUniqueAddr(i * 1337 + 42),
  ts: Date.now() - (14 - i) * 3000,
  punkIndex: (i * 7) % 50,
}));

interface MintingFeedProps {
  mintedCount?: number;
  totalSupply?: number;
  mintProgress?: number;
}

export default function MintingFeed({ mintedCount = 1247, totalSupply = 3333, mintProgress }: MintingFeedProps) {
  const [entries, setEntries] = useState<MintEntry[]>(INITIAL_ENTRIES);
  const [counter, setCounter] = useState(100);
  const [punkUrls, setPunkUrls] = useState<string[]>(DEFAULT_PUNK_IMAGES);
  const listRef = useRef<HTMLDivElement>(null);

  const displayProgress = mintProgress !== undefined
    ? mintProgress
    : Math.round((mintedCount / totalSupply) * 100);

  // Fetch real punk image URLs from GitHub
  useEffect(() => {
    fetchPunkImages().then((urls) => {
      if (urls && urls.length > 0) {
        setPunkUrls(urls);
      }
    });
  }, []);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    const scheduleNext = () => {
      const delay = 300 + Math.floor(Math.random() * 300);
      timeoutId = setTimeout(() => {
        setCounter(c => {
          const newId = c + 1;
          const punkIdx = (newId * 3 + Math.floor(Math.random() * 7)) % 50;
          const newEntry: MintEntry = {
            id: newId,
            tokenId: Math.floor(Math.random() * 3333) + 1,
            addr: genUniqueAddr(newId * 9973 + Date.now() % 9999),
            ts: Date.now(),
            isNew: true,
            punkIndex: punkIdx,
          };
          setEntries(prev => [newEntry, ...prev.slice(0, 21)]);
          return newId;
        });
        scheduleNext();
      }, delay);
    };

    scheduleNext();
    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    if (entries[0]?.isNew) {
      const t = setTimeout(() => {
        setEntries(prev => prev.map((e, i) => i === 0 ? { ...e, isNew: false } : e));
      }, 600);
      return () => clearTimeout(t);
    }
  }, [entries]);

  const timeAgo = (ts: number) => {
    const s = Math.floor((Date.now() - ts) / 1000);
    if (s < 5) return 'just now';
    if (s < 60) return `${s}s ago`;
    return `${Math.floor(s / 60)}m ago`;
  };

  return (
    <div style={{
      background: 'linear-gradient(135deg,rgba(244,183,40,.07) 0%,rgba(62,207,207,.05) 100%)',
      border: '1px solid rgba(244,183,40,.25)',
      borderRadius: 16,
      padding: '16px 18px',
      marginBottom: 28,
      position: 'relative',
      overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute', top: -40, right: -40, width: 180, height: 180,
        background: 'radial-gradient(circle,rgba(244,183,40,.18) 0%,transparent 70%)',
        pointerEvents: 'none',
        animation: 'pulse 2.4s ease-in-out infinite',
      }} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: 'rgba(244,183,40,.15)',
          border: '1px solid rgba(244,183,40,.4)',
          borderRadius: 20,
          padding: '4px 10px',
        }}>
          <span style={{
            width: 7, height: 7, borderRadius: '50%',
            background: '#4ade80',
            display: 'inline-block',
            boxShadow: '0 0 6px #4ade80',
            animation: 'pulse 1.2s ease-in-out infinite',
          }} />
          <span style={{ fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '1.5px', color: '#4ade80', fontWeight: 700 }}>MINTING NOW</span>
        </div>
        <div style={{
          fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '1.2px',
          color: 'rgba(62,207,207,.9)',
          background: 'rgba(62,207,207,.08)',
          border: '1px solid rgba(62,207,207,.2)',
          borderRadius: 20,
          padding: '4px 10px',
        }}>
          ⛓ COMMITTED ON-CHAIN
        </div>
        <div style={{ marginLeft: 'auto', fontFamily: 'var(--mono)', fontSize: 10, color: 'rgba(228,228,229,.35)' }}>
          live feed
        </div>
      </div>

      <div
        ref={listRef}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          maxHeight: 220,
          overflowY: 'auto',
          scrollbarWidth: 'none',
        }}
      >
        {entries.map((entry) => (
          <div
            key={entry.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '7px 10px',
              borderRadius: 10,
              background: entry.isNew
                ? 'rgba(244,183,40,.12)'
                : 'rgba(228,228,229,.03)',
              border: entry.isNew
                ? '1px solid rgba(244,183,40,.3)'
                : '1px solid rgba(228,228,229,.06)',
              transition: 'background .5s ease, border-color .5s ease',
              animation: entry.isNew ? 'mintSlideIn .35s ease' : 'none',
            }}
          >
            <PunkAvatar index={entry.punkIndex} punkUrls={punkUrls} size={28} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 600, color: '#4ade80' }}>ZEC PUNKS — LIVE</span>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'rgba(228,228,229,.35)' }}>#{entry.tokenId}</span>
              </div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'rgba(228,228,229,.45)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {truncateAddr(entry.addr)}
              </div>
            </div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'rgba(228,228,229,.3)', whiteSpace: 'nowrap' }}>
              {timeAgo(entry.ts)}
            </div>
            {entry.isNew && (
              <div style={{
                fontFamily: 'var(--mono)', fontSize: 8, letterSpacing: '1px',
                color: '#4ade80',
                background: 'rgba(74,222,128,.1)',
                border: '1px solid rgba(74,222,128,.25)',
                borderRadius: 4,
                padding: '2px 5px',
                whiteSpace: 'nowrap',
              }}>NEW</div>
            )}
          </div>
        ))}
      </div>

      <div style={{
        marginTop: 12,
        paddingTop: 10,
        borderTop: '1px solid rgba(228,228,229,.07)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: '#4ade80' }}>
          {mintedCount.toLocaleString()} / {totalSupply.toLocaleString()} minted · {displayProgress}%
        </div>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'rgba(244,183,40,.5)' }}>
          Zecpunks.net
        </div>
      </div>
    </div>
  );
}
